/**
 * Composant Gantt — rendu SVG natif, zéro dépendance runtime hors React.
 *
 * Toute la mécanique de calcul (échelle, layout, tracé des dépendances, chemin critique)
 * vit dans des modules purs (`scale.js`, `layout.js`, `dependencies.js`, `critical-path.js`),
 * testés unitairement. Ce fichier ne fait que les appeler et gérer l'état d'interaction
 * (drag, clavier, scroll) — jamais de recalcul de layout à chaque frame de drag : l'état de
 * drag vit en local et ne commit qu'au relâchement (`pointerup`) ou à l'appui clavier.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './GanttStyles.css';
import { SidebarResizer } from './SidebarResizer.jsx';
import {
  readSidebarWidth,
  SIDEBAR_MAX,
  SIDEBAR_MIN,
  SIDEBAR_STORAGE_KEY,
  writeSidebarWidth,
} from './sidebar-width.js';
import { buildDependencyPath, pathToPolylinePoints } from './dependencies.js';
import { assignRowPositions, buildRows, computeVirtualWindow, ROW_HEIGHT } from './layout.js';
import { addDays, createScale, diffDays, isWeekend, ZOOM_LEVELS } from './scale.js';
import { computeCriticalPath } from './critical-path.js';
import { resolveGanttLocale } from './locales.js';

const BAR_HEIGHT = 18;
// Demi-diagonale du jalon (carré tourné de 45°) : 8 → côté 11,3 px, dans la plage 10–12 px
// (palette pastel, 2026-09-28 — un losange de 9 en trait `--line` disparaissait sur la grille).
const MILESTONE_SIZE = 8;
// Anneau de sélection : décalé de 4 px hors de la barre, pour se lire par sa FORME (un cadre
// autour du cadre) et non par sa couleur — le cadre de barre et le chemin critique partagent
// l'encre en thème clair.
const SELECTION_OFFSET = 4;
const RANGE_PADDING_DAYS = 7;

/**
 * Jour calendaire LOCAL de l'utilisateur au format `YYYY-MM-DD`. Jamais `toISOString()` : il
 * convertit en UTC et affiche la veille entre minuit et l'heure du décalage (00:30 à Paris =
 * 22:30 UTC la veille). Aucune conversion de fuseau ensuite : toute la mécanique du Gantt
 * manipule des dates calendaires, pas des instants.
 */
function localTodayISO(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Largeur maximale de la colonne des titres : 60 % de la zone, jamais sous le minimum. */
function maxSidebarWidth(containerWidth) {
  if (!containerWidth) return SIDEBAR_MAX;
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.floor(containerWidth * 0.6)));
}

/** Plage de dates couvrant toutes les tâches, avec un peu d'air de chaque côté. */
function computeRange(tasks, todayISO) {
  const dates = tasks.flatMap((t) => [t.startDate, t.dueDate].filter(Boolean));
  if (dates.length === 0) {
    return { rangeStart: addDays(todayISO, -RANGE_PADDING_DAYS), rangeEnd: addDays(todayISO, 30) };
  }
  const min = dates.reduce((a, b) => (a < b ? a : b));
  const max = dates.reduce((a, b) => (a > b ? a : b));
  return {
    rangeStart: addDays(min, -RANGE_PADDING_DAYS),
    rangeEnd: addDays(max, RANGE_PADDING_DAYS),
  };
}

/** Rectangle {x, width} en pixels pour l'intervalle [startDate, dueDate] (jour de fin inclus). */
function barRect(scale, startDate, dueDate) {
  const x = scale.x(startDate);
  const width = Math.max(scale.x(addDays(dueDate, 1)) - x, 4);
  return { x, width };
}

/**
 * Libellé court d'un repère temporel, adapté au zoom. Toujours des nombres (jour/mois/année) —
 * jamais de nom de mois en dur : le seul mot de la locale est le préfixe de trimestre.
 */
function tickLabel(dateStr, zoom, locale) {
  const [year, month, day] = dateStr.split('-');
  if (zoom === 'day') return String(Number(day));
  if (zoom === 'week') return `${day}/${month}`;
  if (zoom === 'month') return `${month}/${year}`;
  return `${locale.quarterPrefix}${Math.ceil(Number(month) / 3)} ${year}`;
}

/** Pas (en jours) entre deux repères de la règle temporelle, selon le zoom. */
function tickStepDays(zoom) {
  if (zoom === 'day') return 1;
  if (zoom === 'week') return 7;
  if (zoom === 'month') return 30;
  return 90;
}

export function Gantt({
  tasks,
  dependencies = [],
  zoom = 'day',
  onZoomChange,
  today,
  selectedId,
  onSelect,
  onTaskChange,
  onDependencyCreate,
  showCriticalPath = false,
  collapsedIds = [],
  onToggleCollapse,
  readOnly = false,
  // `locale` : `'en'` par défaut (le paquet reste consommable hors du client exiPlan, qui lui
  // passe toujours sa langue courante). `labels` surcharge tout ou partie du dictionnaire —
  // utile pour un appelant qui porte déjà ses propres traductions.
  locale = 'en',
  labels,
  // Clé `localStorage` de la largeur de colonne des titres (préfixée `exiplan-`).
  sidebarStorageKey = SIDEBAR_STORAGE_KEY,
}) {
  const L = useMemo(() => resolveGanttLocale(locale, labels), [locale, labels]);

  // `today` est injectable (testabilité) ; à défaut, seule cette ligne — hors des fonctions
  // pures — touche à l'horloge système, jamais scale/layout/dependencies/critical-path.
  const todayISO = today ?? localTodayISO();

  const bodyRef = useRef(null);
  const layoutRef = useRef(null);
  const headerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [dragState, setDragState] = useState(null); // { id, mode, originStart, originDue, startClientX }
  // Miroir synchrone de `dragState` : le commit au relâchement lit la valeur ici, jamais dans un
  // updater `setState` (qui est rejoué deux fois par StrictMode en développement — un effet de
  // bord y enregistrait le déplacement en double).
  const dragRef = useRef(null);
  const [linkDraft, setLinkDraft] = useState(null); // { predecessorId, x, y }

  const [sidebarWidth, setSidebarWidth] = useState(() => readSidebarWidth(sidebarStorageKey));
  // Largeur de la zone de temps, mesurée (elle change avec la fenêtre ET avec la colonne des
  // titres). 0 tant qu'inconnue (premier rendu, jsdom) : l'échelle retombe sur le zoom seul.
  const [availableWidth, setAvailableWidth] = useState(0);
  // Largeur totale de la zone (colonne + temps) : borne la colonne sur petit écran.
  const [containerWidth, setContainerWidth] = useState(0);
  // Largeur effective : la préférence mémorisée n'est jamais écrasée, elle se réapplique
  // dès que la place revient.
  const sidebarMax = maxSidebarWidth(containerWidth);
  const effectiveSidebarWidth = Math.min(sidebarWidth, sidebarMax);

  const handleSidebarResize = useCallback(
    (width, final) => {
      setSidebarWidth(width);
      if (final) writeSidebarWidth(width, sidebarStorageKey);
    },
    [sidebarStorageKey],
  );

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const layout = layoutRef.current;
    const measure = () => {
      setAvailableWidth(body.clientWidth);
      setContainerWidth(layout?.clientWidth ?? 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    if (layout) observer.observe(layout);
    return () => observer.disconnect();
  }, []);

  const { rangeStart, rangeEnd } = useMemo(() => computeRange(tasks, todayISO), [tasks, todayISO]);
  const scale = useMemo(
    () => createScale({ zoom, rangeStart, rangeEnd, availableWidth }),
    [zoom, rangeStart, rangeEnd, availableWidth],
  );

  const rows = useMemo(() => buildRows(tasks, collapsedIds), [tasks, collapsedIds]);
  const visibleRows = useMemo(() => rows.filter((r) => r.visible), [rows]);
  const positions = useMemo(() => assignRowPositions(rows), [rows]);

  const criticalPath = useMemo(() => {
    if (!showCriticalPath) return null;
    const result = computeCriticalPath({ tasks, dependencies });
    return result.error ? null : result.tasks;
  }, [showCriticalPath, tasks, dependencies]);

  const virtualWindow = useMemo(
    () => computeVirtualWindow({ visibleCount: visibleRows.length, scrollTop, viewportHeight }),
    [visibleRows.length, scrollTop, viewportHeight],
  );
  const paintedRows = visibleRows.slice(virtualWindow.start, virtualWindow.end);

  const totalHeight = visibleRows.length * ROW_HEIGHT;

  // Dates affichées pour une tâche : celles en cours de drag priment sur les dates commises.
  const displayDates = useCallback(
    (task) => {
      if (dragState && dragState.id === task.id) {
        return { startDate: dragState.previewStart, dueDate: dragState.previewDue };
      }
      return { startDate: task.startDate, dueDate: task.dueDate };
    },
    [dragState],
  );

  const handleBodyScroll = useCallback((event) => {
    const { scrollTop: top, scrollLeft, clientHeight } = event.currentTarget;
    setScrollTop(top);
    setViewportHeight(clientHeight);
    if (headerRef.current) headerRef.current.scrollLeft = scrollLeft;
  }, []);

  const commitTaskChange = useCallback(
    (id, startDate, dueDate) => {
      if (readOnly || !onTaskChange) return;
      onTaskChange({ id, startDate, dueDate });
    },
    [readOnly, onTaskChange],
  );

  const startDrag = useCallback(
    (task, mode) => (event) => {
      // Un lot a des dates dérivées de ses enfants : jamais déplaçable (ni souris, ni clavier).
      if (readOnly || task.kind === 'phase') return;
      event.preventDefault();
      const startClientX = event.clientX;

      function setDrag(next) {
        dragRef.current = next;
        setDragState(next);
      }

      setDrag({
        id: task.id,
        mode,
        originStart: task.startDate,
        originDue: task.dueDate,
        previewStart: task.startDate,
        previewDue: task.dueDate,
        startClientX,
      });

      function onMove(moveEvent) {
        const prev = dragRef.current;
        if (!prev) return;
        const deltaPx = moveEvent.clientX - startClientX;
        const deltaDays = Math.round(deltaPx / scale.pxPerDay);
        if (mode === 'move') {
          setDrag({
            ...prev,
            previewStart: addDays(prev.originStart, deltaDays),
            previewDue: addDays(prev.originDue, deltaDays),
          });
        } else if (mode === 'resize-start') {
          const candidate = addDays(prev.originStart, deltaDays);
          setDrag({
            ...prev,
            previewStart: candidate < prev.originDue ? candidate : prev.originDue,
          });
        } else {
          const candidate = addDays(prev.originDue, deltaDays);
          setDrag({
            ...prev,
            previewDue: candidate > prev.previewStart ? candidate : prev.previewStart,
          });
        }
      }

      function onUp() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const final = dragRef.current;
        setDrag(null);
        // Un simple clic (aucun déplacement) n'envoie rien au serveur.
        if (
          final &&
          (final.previewStart !== final.originStart || final.previewDue !== final.originDue)
        ) {
          commitTaskChange(final.id, final.previewStart, final.previewDue);
        }
      }

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [readOnly, scale.pxPerDay, commitTaskChange],
  );

  const startLinkDraft = useCallback(
    (task) => (event) => {
      if (readOnly) return;
      event.preventDefault();
      event.stopPropagation();
      const container = bodyRef.current;

      function pointFromEvent(moveEvent) {
        const rect = container.getBoundingClientRect();
        return {
          x: moveEvent.clientX - rect.left + container.scrollLeft,
          y: moveEvent.clientY - rect.top + container.scrollTop,
        };
      }

      setLinkDraft({ predecessorId: task.id, ...pointFromEvent(event) });

      function onMove(moveEvent) {
        setLinkDraft((prev) => (prev ? { ...prev, ...pointFromEvent(moveEvent) } : prev));
      }

      function onUp(upEvent) {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const point = pointFromEvent(upEvent);
        const targetY = point.y;
        const targetRow = paintedRows.find((row) => {
          const y = positions.get(row.task.id) ?? -1;
          return targetY >= y && targetY < y + ROW_HEIGHT;
        });
        setLinkDraft(null);
        if (targetRow && targetRow.task.id !== task.id && onDependencyCreate) {
          onDependencyCreate({ predecessorId: task.id, successorId: targetRow.task.id });
        }
      }

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [readOnly, onDependencyCreate, paintedRows, positions],
  );

  const handleBarKeyDown = useCallback(
    (task) => (event) => {
      if (readOnly || task.kind === 'phase') return;
      const { startDate, dueDate } = displayDates(task);
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const delta = event.key === 'ArrowLeft' ? -1 : 1;
        event.preventDefault();
        if (event.shiftKey) {
          const candidate = addDays(dueDate, delta);
          if (candidate > startDate) commitTaskChange(task.id, startDate, candidate);
        } else {
          commitTaskChange(task.id, addDays(startDate, delta), addDays(dueDate, delta));
        }
      }
    },
    [readOnly, displayDates, commitTaskChange],
  );

  const ticks = useMemo(() => {
    const step = tickStepDays(zoom);
    const totalDays = Math.max(diffDays(rangeStart, rangeEnd), 1);
    const marks = [];
    for (let offset = 0; offset <= totalDays; offset += step) {
      const dateStr = addDays(rangeStart, offset);
      marks.push({ dateStr, x: scale.x(dateStr) });
    }
    return marks;
  }, [zoom, rangeStart, rangeEnd, scale]);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);

  const weekendBands = useMemo(() => {
    if (zoom !== 'day' && zoom !== 'week') return [];
    const totalDays = Math.max(diffDays(rangeStart, rangeEnd), 1);
    const bands = [];
    for (let offset = 0; offset <= totalDays; offset += 1) {
      const dateStr = addDays(rangeStart, offset);
      if (isWeekend(dateStr)) bands.push({ x: scale.x(dateStr), width: scale.pxPerDay });
    }
    return bands;
  }, [zoom, rangeStart, rangeEnd, scale]);

  const todayX = scale.x(todayISO);

  const barRectsById = useMemo(() => {
    const map = new Map();
    for (const row of visibleRows) {
      const { startDate, dueDate } = displayDates(row.task);
      if (!startDate || !dueDate) continue;
      const y = positions.get(row.task.id) ?? 0;
      const { x, width } = barRect(scale, startDate, dueDate);
      map.set(row.task.id, { x, y: y + (ROW_HEIGHT - BAR_HEIGHT) / 2, width, height: BAR_HEIGHT });
    }
    return map;
  }, [visibleRows, positions, scale, displayDates]);

  return (
    <div className="gantt" role="application" aria-label={L.ganttLabel}>
      <div className="gantt__toolbar">
        <span className="gantt__zoom" role="group" aria-label={L.zoomGroupLabel}>
          {ZOOM_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              className="gantt__zoom-btn"
              aria-pressed={zoom === level}
              onClick={() => onZoomChange?.(level)}
            >
              {L.zoom[level]}
            </button>
          ))}
        </span>
      </div>

      <div className="gantt__body" ref={layoutRef}>
        {/* exiui:allow style-inline — géométrie calculée */}
        <div className="gantt__sidebar" style={{ flexBasis: effectiveSidebarWidth }}>
          <div className="gantt__sidebar-header">{L.tasksHeader}</div>
          <div style={{ overflow: 'hidden', height: `calc(100% - 32px)` }}>
            <div style={{ transform: `translateY(${-scrollTop}px)` }}>
              {paintedRows.map((row) => {
                const { task, depth, hasChildren } = row;
                const y = positions.get(task.id) ?? 0;
                return (
                  <div
                    key={task.id}
                    className={`gantt__row-label${selectedId === task.id ? ' gantt__row-label--selected' : ''}`}
                    style={{
                      paddingLeft: 4 + depth * 16,
                      position: 'absolute',
                      top: y,
                      width: '100%',
                    }}
                    onClick={() => onSelect?.(task.id)}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        className="gantt__row-toggle"
                        aria-expanded={!collapsedIds.includes(task.id)}
                        aria-label={
                          collapsedIds.includes(task.id)
                            ? L.expand(task.title)
                            : L.collapse(task.title)
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleCollapse?.(task.id);
                        }}
                      >
                        {collapsedIds.includes(task.id) ? '▸' : '▾'}
                      </button>
                    ) : (
                      <span className="gantt__row-toggle" aria-hidden="true" />
                    )}
                    {task.reference && <span className="gantt__row-ref">{task.reference}</span>}
                    <span
                      className={`gantt__row-title${task.kind === 'phase' ? ' gantt__row-title--phase' : ''}`}
                    >
                      {task.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <SidebarResizer
          width={effectiveSidebarWidth}
          maxWidth={sidebarMax}
          onResize={handleSidebarResize}
          label={L.resizeSidebarLabel}
        />

        <div className="gantt__timeline">
          <div className="gantt__timeline-header" ref={headerRef} style={{ overflow: 'hidden' }}>
            <svg width={scale.totalWidth} height={32} role="presentation">
              {ticks.map((tick) => (
                <g key={tick.dateStr}>
                  <line x1={tick.x} y1={0} x2={tick.x} y2={32} className="gantt__grid-line" />
                  <text x={tick.x + 4} y={20} className="gantt__tick-label">
                    {tickLabel(tick.dateStr, zoom, L)}
                  </text>
                </g>
              ))}
            </svg>
          </div>

          <div className="gantt__timeline-body" ref={bodyRef} onScroll={handleBodyScroll}>
            {tasks.length === 0 ? (
              <p className="gantt__empty">{L.empty}</p>
            ) : (
              <svg width={scale.totalWidth} height={Math.max(totalHeight, 1)} role="presentation">
                {/* Week-ends : bande pleine `--line-soft` (`.gantt__weekend`), jamais une hachure —
                    réservée aux états vides depuis `@exidis/ui` 1.3.0. L'ancien motif
                    `gantt-hatch` était de toute façon masqué par la règle CSS. */}
                {weekendBands.map((band) => (
                  <rect
                    key={band.x}
                    x={band.x}
                    y={0}
                    width={band.width}
                    height={totalHeight}
                    className="gantt__weekend"
                  />
                ))}

                {paintedRows.map((row) => {
                  const y = positions.get(row.task.id) ?? 0;
                  return (
                    <line
                      key={`div-${row.task.id}`}
                      x1={0}
                      y1={y + ROW_HEIGHT}
                      x2={scale.totalWidth}
                      y2={y + ROW_HEIGHT}
                      className="gantt__row-divider"
                    />
                  );
                })}

                {dependencies.map((dep) => {
                  const from = barRectsById.get(dep.predecessorId);
                  const to = barRectsById.get(dep.successorId);
                  if (!from || !to) return null;
                  const critical =
                    showCriticalPath &&
                    criticalPath?.get(dep.predecessorId)?.critical &&
                    criticalPath?.get(dep.successorId)?.critical;
                  const points = buildDependencyPath({ from, to, type: dep.type });
                  return (
                    <polyline
                      key={dep.id}
                      points={pathToPolylinePoints(points)}
                      className={`gantt__dependency${critical ? ' gantt__dependency--critical' : ''}`}
                    />
                  );
                })}

                {linkDraft && (
                  <line
                    x1={
                      barRectsById.get(linkDraft.predecessorId)?.x +
                        barRectsById.get(linkDraft.predecessorId)?.width || 0
                    }
                    y1={(barRectsById.get(linkDraft.predecessorId)?.y || 0) + BAR_HEIGHT / 2}
                    x2={linkDraft.x}
                    y2={linkDraft.y}
                    className="gantt__dependency-draft"
                  />
                )}

                <line
                  x1={todayX}
                  y1={0}
                  x2={todayX}
                  y2={totalHeight}
                  className="gantt__today-line"
                  aria-hidden="true"
                />

                {paintedRows.map((row) => {
                  const { task } = row;
                  const rect = barRectsById.get(task.id);
                  const critical = criticalPath?.get(task.id)?.critical;
                  const label = L.barLabel({
                    title: task.title,
                    kind: task.kind,
                    status: task.status,
                    progress: numberFormatter.format(task.progress ?? 0),
                  });

                  if (task.kind === 'phase' && rect) {
                    const midY = rect.y + BAR_HEIGHT / 2;
                    return (
                      <g
                        key={task.id}
                        className="gantt__bar"
                        tabIndex={0}
                        role="img"
                        aria-label={label}
                        onKeyDown={handleBarKeyDown(task)}
                        onClick={() => onSelect?.(task.id)}
                      >
                        <line
                          x1={rect.x}
                          y1={midY}
                          x2={rect.x + rect.width}
                          y2={midY}
                          className="gantt__phase-tick"
                        />
                        <line
                          x1={rect.x}
                          y1={rect.y}
                          x2={rect.x}
                          y2={rect.y + BAR_HEIGHT}
                          className="gantt__phase-tick"
                        />
                        <line
                          x1={rect.x + rect.width}
                          y1={rect.y}
                          x2={rect.x + rect.width}
                          y2={rect.y + BAR_HEIGHT}
                          className="gantt__phase-tick"
                        />
                      </g>
                    );
                  }

                  if (task.kind === 'milestone' && rect) {
                    const cx = rect.x;
                    const cy = rect.y + BAR_HEIGHT / 2;
                    const r = MILESTONE_SIZE;
                    const points = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
                    return (
                      <polygon
                        key={task.id}
                        points={points}
                        tabIndex={0}
                        role="img"
                        aria-label={label}
                        className={`gantt__milestone${critical ? ' gantt__milestone--critical' : ''}`}
                        onClick={() => onSelect?.(task.id)}
                      />
                    );
                  }

                  if (!rect) return null;
                  const progressWidth = (rect.width * (task.progress ?? 0)) / 100;
                  return (
                    <g
                      key={task.id}
                      className="gantt__bar"
                      tabIndex={0}
                      role="img"
                      aria-label={label}
                      onKeyDown={handleBarKeyDown(task)}
                      onClick={() => onSelect?.(task.id)}
                    >
                      <rect
                        x={rect.x}
                        y={rect.y}
                        width={rect.width}
                        height={BAR_HEIGHT}
                        className="gantt__bar-track"
                      />
                      <rect
                        x={rect.x}
                        y={rect.y}
                        width={progressWidth}
                        height={BAR_HEIGHT}
                        className="gantt__bar-progress"
                      />
                      <rect
                        x={rect.x}
                        y={rect.y}
                        width={rect.width}
                        height={BAR_HEIGHT}
                        className={`gantt__bar-outline${critical ? ' gantt__bar-outline--critical' : ''}`}
                        onPointerDown={startDrag(task, 'move')}
                      />
                      {selectedId === task.id && (
                        <rect
                          x={rect.x - SELECTION_OFFSET}
                          y={rect.y - SELECTION_OFFSET}
                          width={rect.width + 2 * SELECTION_OFFSET}
                          height={BAR_HEIGHT + 2 * SELECTION_OFFSET}
                          className="gantt__bar-selection"
                          aria-hidden="true"
                        />
                      )}
                      <rect
                        x={rect.x - 3}
                        y={rect.y}
                        width={6}
                        height={BAR_HEIGHT}
                        className="gantt__resize-handle"
                        onPointerDown={startDrag(task, 'resize-start')}
                      />
                      <rect
                        x={rect.x + rect.width - 3}
                        y={rect.y}
                        width={6}
                        height={BAR_HEIGHT}
                        className="gantt__resize-handle"
                        onPointerDown={startDrag(task, 'resize-end')}
                      />
                      <circle
                        cx={rect.x + rect.width}
                        cy={rect.y + BAR_HEIGHT / 2}
                        r={4}
                        className="gantt__link-handle"
                        onPointerDown={startLinkDraft(task)}
                      />
                    </g>
                  );
                })}
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
